import { createContext, useContext, useEffect, useState, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => useContext(AuthContext)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [lawyer, setLawyer] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let lastUserId = null

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const newUserId = session?.user?.id ?? null
      lastUserId = newUserId
      setUser(session?.user ?? null)
      if (session?.user) fetchLawyerProfile(session.user.id)
      else setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id ?? null

      // Skip redundant events: if user id is unchanged, do not refetch lawyer profile.
      // This prevents Supabase reconnect-induced SIGNED_IN events from cascading
      // into a setLawyer that would invalidate authLawyer references downstream.
      if (newUserId === lastUserId && _event !== 'SIGNED_OUT' && _event !== 'USER_UPDATED') {
        return
      }
      lastUserId = newUserId

      setUser(session?.user ?? null)
      if (session?.user) fetchLawyerProfile(session.user.id)
      else {
        setLawyer(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Sync Sentry user identity. On envoie auth_user_id (sub JWT Supabase),
  // PAS lawyer.id ni email. Jointure manuelle au moment du debug Sentry.
  useEffect(() => {
    if (user?.id) {
      Sentry.setUser({ id: user.id, segment: lawyer?.plan || undefined })
    } else {
      Sentry.setUser(null)
    }
  }, [user, lawyer])

  async function fetchLawyerProfile(userId) {
    try {
      // Try matching by auth_user_id first, fallback to email
      let { data, error } = await supabase
        .from('lawyers')
        .select('*')
        .eq('auth_user_id', userId)
        .single()

      if (!data) {
        // Fallback: match by email
        const { data: { user: authUser } } = await supabase.auth.getUser()
        if (authUser?.email) {
          const res = await supabase
            .from('lawyers')
            .select('*')
            .eq('email', authUser.email)
            .single()
          data = res.data
        }
      }

      if (!data) {
        // Auto-réparation : utilisateur authentifié SANS profil lawyer (compte
        // orphelin — provisioning interrompu à l'inscription). On appelle
        // /api/auth/signup (idempotent : crée le profil trial ou renvoie
        // l'existant), puis on relit par auth_user_id. Régularise aussi les
        // orphelins existants à leur prochaine connexion.
        data = await repairLawyerProfile(userId)
      }

      if (data) setLawyer(data)
    } catch (err) {
      console.error('Error fetching lawyer profile:', err)
    } finally {
      setLoading(false)
    }
  }

  // Provisionne (ou récupère) le profil lawyer d'un utilisateur authentifié via
  // l'endpoint idempotent /api/auth/signup, puis relit la ligne. Le serveur
  // dérive l'auth_user_id du JWT (anti-IDOR) : le body ne sert qu'à pré-remplir
  // l'email à la première création. Renvoie la ligne lawyer ou null.
  async function repairLawyerProfile(userId) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const accessToken = session?.access_token
      if (!accessToken) return null

      const resp = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ email: session?.user?.email }),
      })
      if (!resp.ok) return null

      const { data } = await supabase
        .from('lawyers')
        .select('*')
        .eq('auth_user_id', userId)
        .single()
      return data || null
    } catch (err) {
      console.error('Error repairing lawyer profile:', err)
      return null
    }
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  async function signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) throw error
    return data
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    setUser(null)
    setLawyer(null)
  }

  async function resetPassword(email) {
    // redirectTo explicite : sans lui, GoTrue retombe sur le site_url du projet
    // Supabase. L'URL doit aussi figurer dans la Redirect Allow List (Auth →
    // URL Configuration), sinon GoTrue ignore redirectTo.
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    })
    if (error) throw error
    return data
  }

  async function updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
    return data
  }

  const value = useMemo(
    () => ({ user, lawyer, loading, signIn, signUp, signOut, resetPassword, updatePassword }),
    [user, lawyer, loading]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
